import { css, cx } from "@emotion/css";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
import { useObserveElementSize } from "../../../hooks/useObserveElementSize";
import type { IconOnlyButtonProps } from "../../Button";
import { inputDisabledStyles, inputErrorStyles, inputReadOnlyStyles, inputStyles } from "../Primitives/SharedFormStyling.styles";
interface InputProps<T> {
    /**
     * The ID of the input
     */
    id: string;
    /**
     * The input type (text, email, password, etc.)
     */
    type: "text" | "email" | "password" | "tel" | "url" | "search" | "number" | "time";
    /**
     * The value of the input
     */
    value?: T;
    /**
     * The placeholder text to display when input is empty
     */
    placeholder?: string;
    /**
     * Whether the field is disabled
     */
    disabled?: boolean;
    /**
     * Whether the field is required
     */
    required?: boolean;
    /**
     * Whether to autofocus the input
     */
    autoFocus?: boolean;
    /**
     * Whether the input is readonly
     */
    readOnly?: boolean;
    /**
     * The name attribute for the input
     */
    name?: string;
    /**
     * The prefix to display before the input - can be a string or a icon it cannot be interactive
     */
    prefix?: string | React.ReactElement;
    /**
     * The suffix to display after the input - can be a string or a button with ghost importance and medium size only
     */
    suffix?: string | React.ReactElement<IconOnlyButtonProps>;
    /**
     * The minimum value allowed for number inputs
     */
    min?: number;
    /**
     * The maximum value allowed for number inputs
     */
    max?: number;
    /**
     * The step value for number inputs
     */
    step?: number | "any";
    /**
     * The action to perform when the form control field is changed.
     */
    onChange: (newValue: T) => void;
    /**
     * The ID of the element describing the input
     */
    "aria-describedby"?: string;
    /**
     * Whether the field has an invalid value
     */
    "aria-invalid"?: boolean;
}
export function Input<T extends string | number | undefined>(props: InputProps<T>) {
    const { id, value, placeholder, onChange, disabled = false, required = false, type = "text", autoFocus = false, readOnly = false, name, prefix, suffix, "aria-describedby": ariaDescribedBy, "aria-invalid": ariaInvalid } = props;
    // min, max, step only when type is "number"
    const min = props.type === "number" ? props.min : undefined;
    const max = props.type === "number" ? props.max : undefined;
    const step = props.type === "number" ? props.step : undefined;
    const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            onChange(event.target.value as T);
        }
    }, [onChange]);
    // React useId adds colons which are not valid in css custom properties
    const sanitisedInputId = id.replace(/[:;]/g, "");
    const isButtonSuffix = React.isValidElement(suffix);
    // Simple text truncation for prefix and suffix
    const suffixText = typeof suffix === "string" ? (suffix.length > 4 ? suffix.substring(0, 4) : suffix) : null;
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [prefixElement, setPrefixElement] = React.useState<HTMLDivElement | null>(null);
    const [suffixElement, setSuffixElement] = React.useState<HTMLDivElement | null>(null);
    // Determines the width of the prefix and suffix and sets a custom property.
    // This ensures that the size is correct regardless of the prefix/suffic length or content
    useObserveElementSize(prefixElement, ({ width }) => {
        containerRef.current?.style.setProperty(`--${sanitisedInputId}-prefix-width`, `${width}px`);
    }, () => containerRef.current?.style.removeProperty(`--${sanitisedInputId}-prefix-width`));
    useObserveElementSize(suffixElement, ({ width }) => {
        containerRef.current?.style.setProperty(`--${sanitisedInputId}-suffix-width`, `${width}px`);
    }, () => containerRef.current?.style.removeProperty(`--${sanitisedInputId}-suffix-width`));
    const inputPaddingStyles = {
        paddingLeft: prefix ? `var(--${sanitisedInputId}-prefix-width)` : undefined,
        paddingRight: suffix ? `var(--${sanitisedInputId}-suffix-width)` : undefined,
    };
    return (<div ref={containerRef} className={cx(inputContainerStyles)}>
            {prefix && (<div ref={setPrefixElement} className={cx(inputPrefixStyles)}>
                    {prefix ? (<p className={prefixSuffixTextStyles} title={typeof prefix === "string" ? prefix : undefined}>
                            {prefix}
                        </p>) : (prefix)}
                </div>)}
            <input id={id} type={type} name={name} value={value} placeholder={placeholder} disabled={disabled} readOnly={readOnly} aria-readonly={readOnly} aria-required={required} autoFocus={autoFocus} min={min} max={max} step={step} className={cx(textFieldStyles, inputStyles, {
            [inputErrorStyles]: !!ariaInvalid,
            [inputDisabledStyles]: disabled,
            [inputReadOnlyStyles]: readOnly,
        })} style={inputPaddingStyles} onChange={handleChange} aria-invalid={!!ariaInvalid} aria-describedby={ariaDescribedBy}/>

            {suffix && (<div ref={setSuffixElement} className={cx(inputSuffixStyles, {
                [inputSuffixButtonStyles]: isButtonSuffix,
            })}>
                    {suffixText ? (<p className={prefixSuffixTextStyles} title={typeof suffix === "string" ? suffix : undefined}>
                            {suffixText}
                        </p>) : (suffix)}
                </div>)}
        </div>);
}
const inputContainerStyles = css({
    position: "relative",
    display: "grid",
    gridTemplateColumns: "minmax(0, max-content) 1fr minmax(0, max-content)",
    isolation: "isolate",
});
const textFieldStyles = css({
    minHeight: "2rem",
    placeContent: "center",
    gridColumn: "1 / -1",
    gridRow: 1,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
});
const inputPrefixStyles = css({
    font: text.body.regular.large,
    color: themeTokens.color.text.secondary,
    gridColumn: "1 / 2",
    gridRow: 1,
    placeContent: "center",
    padding: `0 ${space[6]} 0 ${space[8]}`,
    zIndex: 1,
});
const inputSuffixStyles = css({
    font: text.body.regular.large,
    color: themeTokens.color.text.secondary,
    gridColumn: "3 / 4",
    gridRow: 1,
    placeContent: "center",
    padding: `0 ${space[8]}`,
    pointerEvents: "none",
});
const prefixSuffixTextStyles = css({
    margin: 0,
});
const inputSuffixButtonStyles = css({
    pointerEvents: "auto",
    padding: 0,
    paddingRight: space[4],
    "button:disabled svg": {
        fill: themeTokens.color.icon.secondary,
    },
});
